import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useEffect } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Modal } from '@wds/modal';
import ManualTagging from './ManualTagging';
import TrainingFlow from './TrainingFlow';
import { OECTrainingStatus, OECDiscardKeywordGenerationModal, OECDiscardKeywordsModal, SORT_BY, TRAINING_STEP, FILTER_BY_TAGGED_STATUS } from './types';
import { updateTheme, deleteTheme, removeResponseFromTheme, addResponseToTheme, removeThemesFromResponse, addThemesToResponse, deleteKeywords, createKeywords, formatKeywords, formatTheme, formatResponse, trackAmplitudeEvent, getResponseThemeIds, getPredictedThemes, getThemeNames, getResponseText, getThemesPerResponse, mergePaginatedResponses, isAutoTaggingComplete, transformKeywords, haveKeywordsChanged, getStatus, isRetrainAllowed, getRetrainData } from './lib';
import { ALL_FILTER_ID, ALL_FILTER_ID_BLANK, RESPONSE_PAGE_SIZE, MAX_SEEN_COUNT_UPDATE_AUTOTAGGING_MODAL, Z_INDEX } from './constants';
import OpenEndedCodingContext from './OpenEndedCodingContext';
import { useGeneralActions, useRetrainingActions, useTrainingActions, useThemesActions, useResponsesActions, useFiltersActions, useFeedbackActions, useThemesReuseActions, useResponses, useThemes, useFeedback, useTraining, useRetraining, useGeneral } from './Context';
import ErrorToast from './shared/ErrorToast';
import { themesForQuestion } from './graph/fetchThemes';
import { keywordsForQuestion } from './graph/fetchKeywords';
import { createThemeForQuestion } from './graph/createTheme';
import { deleteThemeForQuestion } from './graph/deleteTheme';
import { editThemeForQuestion } from './graph/editTheme';
import { responsesForQuestion } from './graph/fetchResponses';
import { createTagsForResponse } from './graph/applyTagsToResponse';
import { deleteTagsForResponse } from './graph/removeTagsFromResponse';
import { sendThemeFeedback } from './graph/sendTagFeedback';
const OpenEndedCodingModal = ({
  show,
  questionText,
  jobId,
  surveyId,
  questionId,
  predictionId,
  stimuli = [],
  autotrainingStatus = OECTrainingStatus.NOT_TRAINED,
  isSolutions = false,
  setShouldRefetch,
  handleClose,
  handleTrainingFinished,
  setShow
}) => {
  // state
  const {
    error,
    updateAutotaggingModalSeenCount
  } = useGeneral();
  const {
    responses,
    responsesTotal,
    responsesLoading,
    responsesError,
    significantChangesResponses
  } = useResponses();
  // const { selectedThemes } = useFilters();
  const {
    themes,
    keywords,
    initialThemes,
    themesLoading,
    themesError,
    keywordsLoading,
    keywordsError,
    themesAdded,
    themesDeleted
  } = useThemes();
  const {
    autotaggingPredictionId
  } = useFeedback();
  const {
    trainingStep,
    showTrainingScreen,
    shouldRefetchOnOpen,
    unsavedKeywords
  } = useTraining();
  const {
    notEnoughThemesCreatedStep,
    trainingStatus,
    notEnoughThemesCreated
  } = useRetraining();
  // actions
  const {
    setError,
    setShowDiscardKeywordGenerationModal,
    setShowDiscardKeywordsModal,
    setMultiple: setMultipleFieldsGeneral
  } = useGeneralActions();
  const {
    setTrainingStatus,
    setMultiple: setMultipleFieldsRetraining
  } = useRetrainingActions();
  const {
    setShouldRefetchOnOpen,
    setMultiple: setMultipleFieldsTraining
  } = useTrainingActions();
  const {
    setKeywords,
    setInitialThemes,
    setThemes,
    setThemesError,
    setThemesLoading,
    setKeywordsError,
    setKeywordsLoading,
    setMultiple: setMultipleFieldsThemes
  } = useThemesActions();
  const {
    setResponses,
    setResponsesPage,
    setResponsesError,
    setResponsesLoading,
    setMultiple: setMultipleFieldsResponses
  } = useResponsesActions();
  const {
    setSelectedThemes,
    setMultiple: setMultipleFieldsFilters
  } = useFiltersActions();
  const {
    setAutotaggingPredictionId,
    setJobId
  } = useFeedbackActions();
  const {
    setSuggestedThemes
  } = useThemesReuseActions();
  const themesPerResponse = useMemo(() => getThemesPerResponse({
    responses
  }), [responses]);
  const firstPage = 0;
  // Queries
  const [fetchResponses, {
    data: responseData,
    error: responsesDataError,
    loading: responsesDataLoading,
    called: fetchResponsesCalled,
    refetch: refetchResponses,
    fetchMore: fetchMoreResponses
  }] = useLazyQuery(responsesForQuestion, {
    variables: {
      surveyId,
      questionId,
      page: firstPage,
      pageSize: RESPONSE_PAGE_SIZE,
      searchPhrase: '',
      isSolutions,
      sortBy: SORT_BY.DATE,
      filterByTaggedStatus: FILTER_BY_TAGGED_STATUS.ALL,
      includeSuggestedTags: true
    },
    notifyOnNetworkStatusChange: true
  });
  const [fetchThemes, {
    data: themeData,
    error: themesDataError,
    loading: themesDataLoading,
    called: fetchThemesCalled,
    refetch: refetchThemes
  }] = useLazyQuery(themesForQuestion, {
    variables: {
      surveyId,
      questionId
    },
    notifyOnNetworkStatusChange: true
  });
  const [getKeywords, {
    data: keywordsData,
    loading: keywordsDataLoading,
    error: keywordsDataError
  }] = useLazyQuery(keywordsForQuestion, {
    variables: {
      surveyId,
      questionId
    },
    notifyOnNetworkStatusChange: true
  });
  // Mutations
  const [createOECTheme] = useMutation(createThemeForQuestion, {
    onCompleted: ({
      createOecQuestionTag: {
        tag,
        trainingStatus: status
      }
    }) => {
      if (!tag) {
        setError(true);
      }
      const formattedTheme = formatTheme(tag);
      // Theme created successfully, update state
      setMultipleFieldsThemes({
        themes: [...themes, formattedTheme],
        keywords: [...keywords, createKeywords({
          theme: formattedTheme
        })]
      });
      setShouldRefetch(true);
      // User can enter a retraining state
      if (status === OECTrainingStatus.REQUIRES_RETRAINING) {
        setTrainingStatus(status);
      }
    },
    onError: () => {
      setError(true);
    }
  });
  const [deleteOECTheme] = useMutation(deleteThemeForQuestion, {
    onCompleted: ({
      deleteOecQuestionTag: {
        success,
        tagId,
        trainingStatus: status
      }
    }) => {
      if (!success) {
        setError(true);
      }
      // Theme deleted successfully, update state
      setMultipleFieldsThemes({
        themes: deleteTheme({
          themes,
          themeId: tagId
        }),
        keywords: deleteKeywords({
          keywords,
          themeId: tagId
        })
      });
      setShouldRefetch(true);
      // User can enter a retraining state
      if (status === OECTrainingStatus.REQUIRES_RETRAINING) {
        setTrainingStatus(status);
      }
    },
    onError: () => {
      setError(true);
    }
  });
  const [editOECTheme] = useMutation(editThemeForQuestion, {
    onCompleted: ({
      editOecQuestionTag: {
        tag,
        trainingStatus: status
      }
    }) => {
      if (!tag) {
        setError(true);
      }
      // Theme updated successfully, update state
      setThemes(updateTheme({
        themes,
        themeId: tag.id,
        name: tag.label
      }));
      setShouldRefetch(true);
      // User can enter a retraining state
      if (status === OECTrainingStatus.REQUIRES_RETRAINING) {
        setTrainingStatus(status);
      }
    },
    onError: () => {
      setError(true);
    }
  });
  const [sendOECThemeFeedback] = useMutation(sendThemeFeedback, {
    onCompleted: () => {},
    onError: () => {}
  });
  const sendTagFeedback = (updatedResponses, updatedThemes, responseTextId) => {
    const responseThemes = getResponseThemeIds({
      responses: updatedResponses,
      responseTextId
    });
    const predictedThemes = getPredictedThemes({
      themes: updatedThemes,
      responseThemes
    });
    void sendOECThemeFeedback({
      variables: {
        input: {
          predictionId: autotaggingPredictionId,
          surveyId,
          responseTextId,
          responseText: getResponseText({
            responses: updatedResponses,
            responseTextId
          }),
          questionId,
          predictedThemes,
          reviewedThemes: getThemeNames({
            themes: updatedThemes,
            themeIds: responseThemes
          })
        }
      }
    });
  };
  const [createOECThemesForResponse] = useMutation(createTagsForResponse, {
    onCompleted: ({
      createOecTagsForResponse: {
        success,
        tagIds,
        responseTextId,
        trainingStatus: status
      }
    }) => {
      if (!success) {
        setError(true);
      }
      const updatedResponses = addThemesToResponse({
        responses,
        themeIds: tagIds,
        responseTextId
      });
      const updatedThemes = addResponseToTheme({
        themes,
        themeIds: tagIds,
        responseTextId
      });
      setResponses(updatedResponses);
      setThemes(updatedThemes);
      setShouldRefetch(true);
      if (autotaggingPredictionId) {
        sendTagFeedback(updatedResponses, updatedThemes, responseTextId);
      }
      // User can enter a retraining state
      if (status === OECTrainingStatus.REQUIRES_RETRAINING) {
        setTrainingStatus(status);
      }
    },
    onError: () => {
      setError(true);
    }
  });
  const [removeOECThemesForResponse] = useMutation(deleteTagsForResponse, {
    onCompleted: ({
      deleteOecTagsForResponse: {
        success,
        tagIds,
        responseTextId,
        trainingStatus: status
      }
    }) => {
      if (!success) {
        setError(true);
      }
      const updatedResponses = removeThemesFromResponse({
        responses,
        themeId: tagIds[0],
        responseTextId
      });
      const updatedThemes = removeResponseFromTheme({
        themes,
        themeIds: tagIds,
        responseTextId
      });
      setResponses(updatedResponses);
      setThemes(updatedThemes);
      setShouldRefetch(true);
      if (autotaggingPredictionId) {
        sendTagFeedback(updatedResponses, updatedThemes, responseTextId);
      }
      // User can enter a retraining state
      if (status === OECTrainingStatus.REQUIRES_RETRAINING) {
        setTrainingStatus(status);
      }
    },
    onError: () => {
      setError(true);
    }
  });
  const deleteThemeHandler = themeId => {
    void deleteOECTheme({
      variables: {
        input: {
          surveyId,
          questionId,
          tagId: themeId
        }
      }
    });
  };
  const updateThemeHandler = (themeId, name) => {
    void editOECTheme({
      variables: {
        input: {
          surveyId,
          questionId,
          tagId: themeId,
          label: name
        }
      }
    });
  };
  const createThemeHandler = name => {
    void createOECTheme({
      variables: {
        input: {
          surveyId,
          questionId,
          label: name
        }
      }
    });
  };
  const handleRemoveThemesFromResponse = (themeId, responseTextId) => {
    void removeOECThemesForResponse({
      variables: {
        input: {
          surveyId,
          questionId,
          tagIds: [themeId],
          responseTextId
        }
      }
    });
  };
  const handleAddThemesToResponse = (themeIds, responseTextId) => {
    void createOECThemesForResponse({
      variables: {
        input: {
          surveyId,
          questionId,
          tagIds: themeIds,
          responseTextId
        }
      }
    });
  };
  const handleCreateThemeForResponse = (name, responseTextId) => {
    // Create the theme
    void createOECTheme({
      variables: {
        input: {
          surveyId,
          questionId,
          label: name
        }
      }
    })
    // Apply the theme to the response
    .then(({
      data
    }) => {
      var _a;
      const tagId = ((_a = data === null || data === void 0 ? void 0 : data.createOecQuestionTag) === null || _a === void 0 ? void 0 : _a.tag.id) || '';
      void createOECThemesForResponse({
        variables: {
          input: {
            surveyId,
            questionId,
            tagIds: [tagId],
            responseTextId
          }
        }
      });
    });
  };
  const setResponsesPagination = page => {
    var _a;
    setResponsesPage(page);
    const responsesLoaded = (_a = responseData === null || responseData === void 0 ? void 0 : responseData.getOecResponses) === null || _a === void 0 ? void 0 : _a.items.length;
    const lastPage = responsesLoaded / RESPONSE_PAGE_SIZE - 1;
    // only fetch more responses if it's a new page
    if (page > lastPage) {
      fetchMoreResponses && void fetchMoreResponses({
        variables: {
          page,
          pageSize: RESPONSE_PAGE_SIZE
        },
        updateQuery: (prevData, {
          fetchMoreResult: newData
        }) => {
          if (!newData) {
            return prevData;
          }
          return mergePaginatedResponses({
            prevData,
            newData,
            themesPerResponse
          });
        }
      });
    }
  };
  const searchResponses = ({
    search,
    sortBy,
    filterByTaggedStatus,
    themeIds,
    stimulusIds,
    sentiments
  }) => {
    // need to use quotes to make it an exact search for responses
    refetchResponses && void refetchResponses(Object.assign(Object.assign(Object.assign({
      surveyId,
      questionId,
      page: firstPage,
      pageSize: RESPONSE_PAGE_SIZE,
      searchPhrase: search,
      isSolutions,
      sortBy,
      filterByTaggedStatus,
      includeSuggestedTags: true
    }, themeIds && {
      selectedThemes: themeIds
    }), stimulusIds && {
      selectedStimuli: stimulusIds
    }), sentiments && {
      selectedSentiment: sentiments
    })).then(() => {
      setResponsesPage(firstPage);
    });
  };
  const reload = useCallback(({
    resetInitialThemes = false
  }) => {
    // when reloading need to reset the filters/pagination
    setMultipleFieldsFilters({
      searchInput: '',
      sortValue: SORT_BY.DATE,
      filterByTaggedStatus: FILTER_BY_TAGGED_STATUS.ALL,
      filterDisabled: false,
      selectedThemes: [ALL_FILTER_ID, ALL_FILTER_ID_BLANK, ...themes.map(({
        id
      }) => id)],
      selectedStimuli: stimuli.map(({
        id
      }) => id)
    });
    setResponsesPage(firstPage);
    if (resetInitialThemes) {
      setInitialThemes([]);
    }
    refetchResponses && void refetchResponses(Object.assign({
      surveyId,
      questionId,
      page: firstPage,
      pageSize: RESPONSE_PAGE_SIZE,
      searchPhrase: '',
      isSolutions,
      sortBy: SORT_BY.DATE,
      filterByTaggedStatus: FILTER_BY_TAGGED_STATUS.ALL,
      includeSuggestedTags: true
    }, {
      selectedThemes: undefined,
      selectedStimuli: undefined,
      selectedSentiment: undefined
    }));
    // Need to refetch themes as these include the new autogenerated tag IDs
    refetchThemes && void refetchThemes();
    getKeywords();
  }, [isSolutions, questionId, stimuli, surveyId, themes, getKeywords, refetchResponses, refetchThemes, setInitialThemes, setMultipleFieldsFilters, setResponsesPage]);
  // execute queries on open
  useEffect(() => {
    if (show) {
      if (!fetchResponsesCalled) {
        fetchResponses();
      }
      if (!fetchThemesCalled) {
        fetchThemes();
      }
    }
  }, [show, fetchResponsesCalled, fetchThemesCalled, fetchResponses, fetchThemes]);
  // handle responses being fetched
  useEffect(() => {
    const result = responseData === null || responseData === void 0 ? void 0 : responseData.getOecResponses;
    // loading
    if (!!responsesDataLoading !== responsesLoading) {
      setResponsesLoading(!!responsesDataLoading);
    }
    // error
    if (!!responsesDataError !== responsesError) {
      setResponsesError(!!responsesDataError);
    }
    if (result && 'items' in result && !responsesDataLoading) {
      const formattedResponses = result.items.map(r => formatResponse(r));
      setMultipleFieldsResponses(Object.assign(Object.assign(Object.assign({
        responses: formattedResponses,
        responsesCount: result.totalCount
      }, !responsesTotal && {
        responsesTotal: result.totalCount
      }), {
        showSA: result.optedIn && result.saEnabled
      }), {
        saStats: result.saStats
      }));
      // if there are suggested tags, update them for each response
      const suggested = result.items.reduce((acc, r) => {
        const {
          id,
          suggestedTags
        } = r;
        if (suggestedTags) {
          return Object.assign(Object.assign({}, acc), {
            [id]: suggestedTags
          });
        }
        return acc;
      }, {});
      setSuggestedThemes(suggested);
    }
  }, [responseData, responsesDataLoading, responsesDataError, responsesLoading, responsesError, responsesTotal, setMultipleFieldsResponses, setResponsesLoading, setResponsesError, setSuggestedThemes]);
  // handle themes being fetched
  useEffect(() => {
    const result = themeData === null || themeData === void 0 ? void 0 : themeData.getOecQuestionTags;
    // loading
    if (!!themesDataLoading !== themesLoading) {
      setThemesLoading(!!themesDataLoading);
    }
    // error
    if (!!themesDataError !== themesError) {
      setThemesError(!!themesDataError);
    }
    if (result && result.length > 0 && !themesDataLoading) {
      const formattedThemes = result.map(t => formatTheme(t));
      setMultipleFieldsThemes(Object.assign({
        themes: formattedThemes
      }, initialThemes.length === 0 && {
        initialThemes: formattedThemes
      }));
      setSelectedThemes([ALL_FILTER_ID, ALL_FILTER_ID_BLANK, ...formattedThemes.map(t => t.id)]);
    }
  }, [themeData, themesDataLoading, themesDataError, themesLoading, themesError, initialThemes, setMultipleFieldsThemes, setSelectedThemes, setThemesLoading, setThemesError]);
  // handle keywords being fetched
  useEffect(() => {
    var _a;
    const result = (_a = keywordsData === null || keywordsData === void 0 ? void 0 : keywordsData.getOecKeywords) === null || _a === void 0 ? void 0 : _a.tags;
    // loading
    if (!!keywordsDataLoading !== keywordsLoading) {
      setKeywordsLoading(!!keywordsDataLoading);
    }
    // error
    if (!!keywordsDataError !== keywordsError) {
      setKeywordsError(!!keywordsDataError);
    }
    if (result && result.length > 0 && !keywordsDataLoading) {
      const kw = result.map(k => formatKeywords(k));
      setKeywords(kw);
    }
  }, [keywordsData, keywordsDataLoading, keywordsDataError, keywordsLoading, keywordsError, setKeywords, setMultipleFieldsThemes, setKeywordsError, setKeywordsLoading]);
  // handle setting initially autotaggingPredictionId
  useEffect(() => {
    if (!autotaggingPredictionId && predictionId) {
      setAutotaggingPredictionId(predictionId);
    }
  }, [autotaggingPredictionId, predictionId, setAutotaggingPredictionId]);
  // if a job id is passed, there is a currently running autotagging job
  useEffect(() => {
    if (!jobId) {
      return;
    }
    setJobId(jobId);
    setMultipleFieldsTraining({
      showTrainingScreen: true,
      trainingStep: TRAINING_STEP.TRAINING_IN_PROGRESS
    });
  }, [jobId, setJobId, setMultipleFieldsTraining]);
  // ensure training status is synced
  useEffect(() => {
    // the user hasn't completed autotagging
    if (!isAutoTaggingComplete(autotrainingStatus)) {
      return;
    }
    // the training status needs to be synced as autotagging is complete
    if (!isAutoTaggingComplete(trainingStatus)) {
      setTrainingStatus(autotrainingStatus);
    }
  }, [autotrainingStatus, trainingStatus, setTrainingStatus]);
  // retraining stuff
  useEffect(() => {
    const status = getStatus({
      responsesTotal,
      themes
    });
    setMultipleFieldsRetraining({
      notEnoughThemesCreated: status.notEnoughThemesCreated
    });
  }, [responsesTotal, themes, setMultipleFieldsRetraining]);
  // retraining stuff
  useEffect(() => {
    const retrainData = getRetrainData({
      prevThemes: initialThemes,
      newThemes: themes
    });
    setMultipleFieldsThemes({
      themesAdded: retrainData.themesAdded,
      themesChanged: retrainData.themesChanged,
      themesDeleted: retrainData.themesDeleted
    });
    setMultipleFieldsResponses({
      significantChangesResponses: retrainData.significantChangesResponses,
      responsesChanged: retrainData.responsesChanged
    });
  }, [themes, initialThemes, setMultipleFieldsThemes, setMultipleFieldsResponses]);
  useEffect(() => {
    // we need to refetch themes and responses
    if (show && shouldRefetchOnOpen) {
      setShouldRefetchOnOpen(false);
      reload({});
    }
  }, [show, shouldRefetchOnOpen, setShouldRefetchOnOpen, reload]);
  // TODO: Extract to separate functions or another file + test
  const handleOecModalClose = () => {
    const keywordsChanged = haveKeywordsChanged({
      generatedKeywords: transformKeywords({
        keywords
      }),
      unsavedKeywords
    });
    // 1. Check should we show the discard keywords modal
    if (keywordsChanged && showTrainingScreen) {
      setShowDiscardKeywordsModal(OECDiscardKeywordsModal.SHOW_KEYWORDS_MODAL_CLOSED);
      return;
    }
    // 2. Check should we show the discard keywords generation modal
    if (trainingStep === TRAINING_STEP.KEYWORD_FETCHING && showTrainingScreen || trainingStep === TRAINING_STEP.KEYWORD_GENERATION && showTrainingScreen) {
      setShowDiscardKeywordGenerationModal(OECDiscardKeywordGenerationModal.SHOW_KEYWORDS_MODAL_CLOSED);
      return;
    }
    const retrainAllowed = isRetrainAllowed({
      trainingStatus,
      themesAdded,
      themesDeleted,
      significantChangesResponses,
      notEnoughThemesCreated,
      notEnoughThemesCreatedStep
    });
    // 3. Check should we show the update autotagging modal
    if (trainingStatus !== OECTrainingStatus.NOT_TRAINED && retrainAllowed && !showTrainingScreen && updateAutotaggingModalSeenCount < MAX_SEEN_COUNT_UPDATE_AUTOTAGGING_MODAL) {
      setMultipleFieldsGeneral({
        updateAutotaggingModalSeenCount: updateAutotaggingModalSeenCount + 1,
        showUpdateAutotaggingModal: true
      });
      return;
    }
    // 4. Run any parent on close handlers
    handleClose();
    // 5. Need to refetch when opening, and show the manual tagging screen
    if (isAutoTaggingComplete(trainingStatus) && trainingStep === TRAINING_STEP.TRAINING_FINISHED) {
      setMultipleFieldsTraining({
        shouldRefetchOnOpen: true,
        showTrainingScreen: false,
        trainingStep: TRAINING_STEP.KEYWORD_GENERATION
      });
    }
    // 6. Trigger an amplitude event for the user closing the modal
    trackAmplitudeEvent({
      amplitudeEvent: 'open-ended coding exited modal',
      data: {
        surveyId,
        questionId,
        pageName: isSolutions ? 'ctm' : 'analyze'
      }
    });
  };
  return _jsx(OpenEndedCodingContext.Provider, {
    value: {
      // handlers
      filterResponses: searchResponses,
      setShowOecModal: setShow,
      setResponsesPage: setResponsesPagination,
      deleteThemeHandler,
      updateThemeHandler,
      createThemeHandler,
      handleRemoveThemesFromResponse,
      handleAddThemesToResponse,
      handleCreateThemeForResponse,
      handleTrainingFinished,
      reloadResponses: () => reload({
        resetInitialThemes: true
      }),
      getKeywords,
      setShouldRefetch
    },
    children: _jsxs(Modal, {
      show: show,
      size: "full",
      "data-testid": "OpenEndedCoding",
      onClose: handleOecModalClose,
      elevation: Z_INDEX,
      children: [!showTrainingScreen ? _jsx(ManualTagging, {
        questionText: questionText,
        handleClose: handleClose
      }) : _jsx(TrainingFlow, {
        questionText: questionText,
        show: show
      }), _jsx(ErrorToast, {
        show: error,
        autoClose: true,
        onClose: () => {
          setError(false);
        }
      })]
    })
  });
};
export { OpenEndedCodingModal as default };